import React, { useState, Children, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X, Zap, RefreshCw, Target, BarChart3, ArrowRight, ShoppingBag, Smartphone, FileText, Clock, ShieldCheck } from 'lucide-react';
// Color Palette Constants
const COLORS = {
  cream: '#FDF8F5',
  navy: '#1D3557',
  coral: '#E63946',
  lightCoral: '#FAD4D8',
  white: '#FFFFFF',
  gray: '#4A5568'
};
// Animation Variants
const fadeIn = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
// Modal Component
interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}
function Modal({
  isOpen,
  onClose,
  title,
  children
}: ModalProps) {
  if (!isOpen) return null;
  return <AnimatePresence data-id="element-1">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={onClose} data-id="element-2">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" data-id="element-3" />

          {/* Modal Content */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.2
      }} className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-4">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-100" data-id="element-5">
              <h2 className="text-2xl font-bold text-[#1D3557]" data-id="element-6">{title}</h2>
              <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors" data-id="element-7">
                <X className="w-6 h-6 text-[#4A5568]" data-id="element-8" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 overflow-y-auto max-h-[60vh] text-[#4A5568] leading-relaxed" data-id="element-9">
              {children}
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
// Privacy Policy Content
function PrivacyContent() {
  return <div className="space-y-6" data-id="element-10">
      <p className="text-sm text-[#9CA3AF]" data-id="element-11">
        Última actualización:{' '}
        {new Date().toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })}
      </p>

      <section data-id="element-12">
        <h3 className="text-lg font-bold text-[#1D3557] mb-3" data-id="element-13">
          1. Información que Recopilamos
        </h3>
        <p data-id="element-14">
          Recopilamos información que nos proporcionas directamente cuando
          realizas una compra, incluyendo:
        </p>
        <ul className="list-disc pl-6 mt-2 space-y-1" data-id="element-15">
          <li data-id="element-16">Nombre y dirección de correo electrónico</li>
          <li data-id="element-17">
            Información de pago (procesada de forma segura por nuestro proveedor
            de pagos)
          </li>
        </ul>
      </section>

      <section data-id="element-18">
        <h3 className="text-lg font-bold text-[#1D3557] mb-3" data-id="element-19">
          2. Uso de la Información
        </h3>
        <p data-id="element-20">Utilizamos la información recopilada para:</p>
        <ul className="list-disc pl-6 mt-2 space-y-1" data-id="element-21">
          <li data-id="element-22">Procesar y entregar tu compra</li>
          <li data-id="element-23">Enviarte el PDF y materiales relacionados</li>
          <li data-id="element-24">Comunicarnos contigo sobre tu pedido</li>
          <li data-id="element-25">Mejorar nuestros productos y servicios</li>
        </ul>
      </section>

      <section data-id="element-26">
        <h3 className="text-lg font-bold text-[#1D3557] mb-3" data-id="element-27">
          3. Protección de Datos
        </h3>
        <p data-id="element-28">
          Implementamos medidas de seguridad técnicas y organizativas para
          proteger tu información personal contra acceso no autorizado,
          alteración, divulgación o destrucción.
        </p>
      </section>

      <section data-id="element-29">
        <h3 className="text-lg font-bold text-[#1D3557] mb-3" data-id="element-30">
          4. Tus Derechos
        </h3>
        <p data-id="element-31">Tienes derecho a:</p>
        <ul className="list-disc pl-6 mt-2 space-y-1" data-id="element-32">
          <li data-id="element-33">Acceder a tus datos personales</li>
          <li data-id="element-34">Rectificar datos inexactos</li>
          <li data-id="element-35">Solicitar la eliminación de tus datos</li>
          <li data-id="element-36">Oponerte al procesamiento de tus datos</li>
        </ul>
      </section>

      <section data-id="element-37">
        <h3 className="text-lg font-bold text-[#1D3557] mb-3" data-id="element-38">5. Cookies</h3>
        <p data-id="element-39">
          Utilizamos cookies esenciales para el funcionamiento del sitio y
          cookies analíticas para entender cómo los visitantes interactúan con
          nuestro sitio web.
        </p>
      </section>
    </div>;
}
// Terms of Service Content
function TermsContent() {
  return <div className="space-y-6" data-id="element-40">
      <p className="text-sm text-[#9CA3AF]" data-id="element-41">
        Última actualización:{' '}
        {new Date().toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })}
      </p>

      <section data-id="element-42">
        <h3 className="text-lg font-bold text-[#1D3557] mb-3" data-id="element-43">
          1. Aceptación de los Términos
        </h3>
        <p data-id="element-44">
          Al comprar y acceder a "El Mini-Lab de Ingresos Extra", aceptas estos
          términos y condiciones en su totalidad. Si no estás de acuerdo con
          estos términos, no realices la compra.
        </p>
      </section>

      <section data-id="element-45">
        <h3 className="text-lg font-bold text-[#1D3557] mb-3" data-id="element-46">
          2. Descripción del Producto
        </h3>
        <p data-id="element-47">
          "El Mini-Lab de Ingresos Extra" es un producto digital en formato PDF
          que incluye:
        </p>
        <ul className="list-disc pl-6 mt-2 space-y-1" data-id="element-48">
          <li data-id="element-49">Guía de 7 días para generar ingresos extra</li>
          <li data-id="element-50">Plantillas y scripts de mensajes</li>
          <li data-id="element-51">Checklists y herramientas de medición</li>
        </ul>
      </section>

      <section data-id="element-52">
        <h3 className="text-lg font-bold text-[#1D3557] mb-3" data-id="element-53">
          3. Entrega del Producto
        </h3>
        <p data-id="element-54">
          Una vez completada la compra, recibirás acceso inmediato al PDF a
          través del correo electrónico proporcionado. Es tu responsabilidad
          proporcionar una dirección de correo válida.
        </p>
      </section>

      <section data-id="element-55">
        <h3 className="text-lg font-bold text-[#1D3557] mb-3" data-id="element-56">
          4. Política de Reembolso
        </h3>
        <p data-id="element-57">
          Debido a la naturaleza digital del producto, todas las ventas son
          finales. No ofrecemos reembolsos una vez que el producto ha sido
          entregado y descargado.
        </p>
      </section>

      <section data-id="element-58">
        <h3 className="text-lg font-bold text-[#1D3557] mb-3" data-id="element-59">
          5. Propiedad Intelectual
        </h3>
        <p data-id="element-60">
          Todo el contenido del Mini-Lab está protegido por derechos de autor.
          Al comprar, obtienes una licencia personal e intransferible para usar
          el material. Queda prohibido:
        </p>
        <ul className="list-disc pl-6 mt-2 space-y-1" data-id="element-61">
          <li data-id="element-62">Redistribuir o revender el contenido</li>
          <li data-id="element-63">Compartir el PDF con terceros</li>
          <li data-id="element-64">Usar el contenido para crear productos derivados</li>
        </ul>
      </section>

      <section data-id="element-65">
        <h3 className="text-lg font-bold text-[#1D3557] mb-3" data-id="element-66">
          6. Limitación de Responsabilidad
        </h3>
        <p data-id="element-67">
          Los resultados pueden variar. No garantizamos ingresos específicos. El
          éxito depende de tu esfuerzo, dedicación y circunstancias
          individuales.
        </p>
      </section>

      <section data-id="element-68">
        <h3 className="text-lg font-bold text-[#1D3557] mb-3" data-id="element-69">
          7. Modificaciones
        </h3>
        <p data-id="element-70">
          Nos reservamos el derecho de modificar estos términos en cualquier
          momento. Los cambios entrarán en vigor inmediatamente después de su
          publicación.
        </p>
      </section>
    </div>;
}
export function SalesPage() {
  const [activeModal, setActiveModal] = useState<'privacy' | 'terms' | null>(null);
  const scrollToPricing = () => {
    document.getElementById('pricing')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const openPrivacy = () => setActiveModal('privacy');
  const openTerms = () => setActiveModal('terms');
  const closeModal = () => setActiveModal(null);
  return <div className="min-h-screen font-sans selection:bg-[#E63946] selection:text-white" style={{
    backgroundColor: COLORS.cream,
    color: COLORS.navy
  }} data-id="element-71">
      {/* Modals */}
      <Modal isOpen={activeModal === 'privacy'} onClose={closeModal} title="Política de Privacidad" data-id="element-72">
        <PrivacyContent data-id="element-73" />
      </Modal>
      <Modal isOpen={activeModal === 'terms'} onClose={closeModal} title="Términos y Condiciones" data-id="element-74">
        <TermsContent data-id="element-75" />
      </Modal>

      {/* Sticky Header */}
      <motion.header initial={{
      y: -100
    }} animate={{
      y: 0
    }} className="sticky top-0 z-50 w-full border-b border-opacity-10 backdrop-blur-md bg-[#FDF8F5]/90 border-[#1D3557]" data-id="element-76">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex justify-between items-center" data-id="element-77">
          <div className="font-bold text-lg sm:text-xl tracking-tight text-[#1D3557]" data-id="element-78">
            Mini-Lab <span className="text-[#E63946]" data-id="element-79">Ingresos Extra</span>
          </div>
          <button onClick={scrollToPricing} className="bg-[#E63946] hover:bg-[#D62839] text-white px-4 py-2 rounded-full text-sm font-medium transition-colors shadow-sm" data-id="element-80">
            Obtener PDF ($19)
          </button>
        </div>
      </motion.header>

      {/* HERO SECTION */}
      <section className="relative pt-12 pb-20 px-4 sm:px-6 overflow-hidden" data-id="element-81">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-82">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="flex flex-col gap-6 z-10" data-id="element-83">
            <motion.div variants={fadeIn} data-id="element-84">
              <span className="inline-block px-3 py-1 rounded-full bg-[#E63946]/10 text-[#E63946] text-sm font-semibold mb-4" data-id="element-85">
                Para mujeres 35+ que quieren resultados, no humo
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight tracking-tight text-[#1D3557] mb-4" data-id="element-86">
                Tu primer ingreso extra en 7 días{' '}
                <span className="text-[#E63946]" data-id="element-87">(sin humo)</span>
              </h1>
              <p className="text-xl sm:text-2xl text-[#4A5568] font-medium leading-relaxed" data-id="element-88">
                No necesitas otra idea bonita.{' '}
                <br className="hidden sm:block" data-id="element-89" />
                Necesitas{' '}
                <span className="bg-[#FAD4D8] px-1" data-id="element-90">una idea que pague</span>.
              </p>
            </motion.div>

            <motion.p variants={fadeIn} className="text-lg text-[#4A5568] leading-relaxed max-w-lg" data-id="element-91">
              Si tienes 35+ y vives entre trabajo, casa, familia, cansancio y
              mil pendientes… lo último que te sirve es un "curso" de 8 semanas
              que te promete libertad financiera mientras te pide 3 horas
              diarias.
            </motion.p>

            <motion.div variants={fadeIn} className="flex flex-col sm:flex-row gap-4 mt-4" data-id="element-92">
              <button onClick={scrollToPricing} className="bg-[#E63946] hover:bg-[#D62839] text-white text-lg font-bold px-8 py-4 rounded-lg shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1 flex items-center justify-center gap-2" data-id="element-93">
                Quiero el Mini-Lab <ArrowRight className="w-5 h-5" data-id="element-94" />
              </button>
              <div className="flex items-center justify-center gap-2 text-sm text-[#4A5568] mt-2 sm:mt-0" data-id="element-95">
                <ShieldCheck className="w-5 h-5 text-[#1D3557]" data-id="element-96" />
                <span data-id="element-97">Pago único de $19</span>
              </div>
            </motion.div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="relative z-0" data-id="element-98">
            <div className="absolute inset-0 bg-[#E63946] rounded-3xl opacity-5 blur-3xl transform rotate-3 scale-105" data-id="element-99"></div>
            <img src="https://cdn.magicpatterns.com/uploads/fLb7VjkwHxiPqaQ3UtFe75/MiniLabDeIngresosExtra1.png" alt="El Mini-Lab de Ingresos Extra en tablet y móvil" className="relative rounded-2xl shadow-2xl w-full border-4 border-white" data-id="element-100" />
          </motion.div>
        </div>
      </section>

      {/* PROBLEM SECTION */}
      <section className="py-20 px-4 sm:px-6 bg-white" data-id="element-101">
        <div className="max-w-3xl mx-auto" data-id="element-102">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} className="text-center mb-12" data-id="element-103">
            <h2 className="text-3xl font-bold text-[#1D3557] mb-6" data-id="element-104">
              El problema real (y nadie te lo dice)
            </h2>
            <p className="text-lg text-[#4A5568] leading-relaxed" data-id="element-105">
              La mayoría de las mujeres no fallan porque "no son buenas para
              vender".
            </p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="space-y-6" data-id="element-106">
            <div className="bg-[#FDF8F5] p-6 rounded-xl border-l-4 border-[#E63946]" data-id="element-107">
              <p className="text-lg text-[#1D3557] font-medium" data-id="element-108">
                Fallas porque:
              </p>
              <ul className="mt-4 space-y-4" data-id="element-109">
                <li className="flex items-start gap-3" data-id="element-110">
                  <X className="w-6 h-6 text-[#E63946] flex-shrink-0 mt-1" data-id="element-111" />
                  <span className="text-[#4A5568]" data-id="element-112">
                    Te dan <strong data-id="element-113">ideas genéricas</strong> (y tú necesitas algo
                    ejecutable).
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-114">
                  <X className="w-6 h-6 text-[#E63946] flex-shrink-0 mt-1" data-id="element-115" />
                  <span className="text-[#4A5568]" data-id="element-116">
                    Te piden <strong data-id="element-117">motivación</strong> (y tú necesitas
                    estructura).
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-118">
                  <X className="w-6 h-6 text-[#E63946] flex-shrink-0 mt-1" data-id="element-119" />
                  <span className="text-[#4A5568]" data-id="element-120">
                    Te dicen "haz contenido" (y tú necesitas{' '}
                    <strong data-id="element-121">dinero</strong>).
                  </span>
                </li>
              </ul>
            </div>

            <motion.div variants={fadeIn} className="text-center pt-8" data-id="element-122">
              <p className="text-xl text-[#1D3557] font-medium" data-id="element-123">
                Y cuando no ves resultados rápido, piensas que el problema eres
                tú.
              </p>
              <p className="text-2xl font-bold text-[#E63946] mt-4" data-id="element-124">
                No. El problema es el método.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* SOLUTION SECTION */}
      <section className="py-20 px-4 sm:px-6 bg-[#1D3557] text-white" data-id="element-125">
        <div className="max-w-4xl mx-auto text-center" data-id="element-126">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} data-id="element-127">
            <h2 className="text-3xl sm:text-4xl font-bold mb-8" data-id="element-128">
              Lo que cambia con El Mini-Lab
            </h2>
            <p className="text-xl sm:text-2xl leading-relaxed mb-10 opacity-90" data-id="element-129">
              En lugar de "encuentra tu pasión", aquí hacemos algo más adulto:
            </p>

            <div className="bg-white/10 backdrop-blur-sm p-8 rounded-2xl border border-white/20 inline-block" data-id="element-130">
              <p className="text-2xl sm:text-3xl font-bold text-[#FAD4D8]" data-id="element-131">
                Probamos una cosa por 7 días,
                <br data-id="element-132" />
                con números simples, y ajustamos.
              </p>
            </div>

            <p className="mt-10 text-lg opacity-80 max-w-2xl mx-auto" data-id="element-133">
              Porque cuando no tienes tiempo, no puedes darte el lujo de
              improvisar.
            </p>
          </motion.div>
        </div>
      </section>

      {/* PRODUCT PRESENTATION */}
      <section className="py-20 px-4 sm:px-6 bg-[#FDF8F5]" data-id="element-134">
        <div className="max-w-6xl mx-auto" data-id="element-135">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-136">
            <h2 className="text-3xl font-bold text-[#1D3557] mb-6" data-id="element-137">
              3 Caminos Probados
            </h2>
            <p className="text-lg text-[#4A5568]" data-id="element-138">
              <strong data-id="element-139">El Mini-Lab de Ingresos Extra (7 días)</strong> es un PDF
              práctico (sin relleno) que te guía para activar un primer ingreso
              extra usando 1 de 3 micro-experimentos:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-140">
            {[{
            icon: <Zap className="w-10 h-10 text-[#E63946]" data-id="element-141" />,
            title: 'Servicio Rápido',
            desc: 'Dinero más rápido. Ideal si necesitas cashflow inmediato.',
            color: 'bg-white'
          }, {
            icon: <ShoppingBag className="w-10 h-10 text-[#E63946]" data-id="element-142" />,
            title: 'Reventa Inteligente',
            desc: 'Sin inventario grande. Mueve productos sin complicarte.',
            color: 'bg-white'
          }, {
            icon: <Smartphone className="w-10 h-10 text-[#E63946]" data-id="element-143" />,
            title: 'Producto Digital Simple',
            desc: 'Sin ser experta. Empaqueta lo que sabes en algo simple.',
            color: 'bg-white'
          }].map((item, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className={`${item.color} p-8 rounded-2xl shadow-sm border border-[#E63946]/10 hover:shadow-md transition-shadow text-center`} data-id="element-144">
                <div className="bg-[#FDF8F5] w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-145">
                  {item.icon}
                </div>
                <h3 className="text-xl font-bold text-[#1D3557] mb-3" data-id="element-146">
                  {item.title}
                </h3>
                <p className="text-[#4A5568]" data-id="element-147">{item.desc}</p>
              </motion.div>)}
          </div>

          <div className="text-center mt-12" data-id="element-148">
            <p className="text-lg font-medium text-[#1D3557]" data-id="element-149">
              Tú eliges uno. Yo te doy el plan diario, los scripts y el sistema
              de medición.
            </p>
          </div>
        </div>
      </section>

      {/* CONTENTS SECTION */}
      <section className="py-20 px-4 sm:px-6 bg-white" data-id="element-150">
        <div className="max-w-4xl mx-auto" data-id="element-151">
          <div className="bg-[#1D3557] rounded-3xl p-8 sm:p-12 text-white shadow-xl relative overflow-hidden" data-id="element-152">
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#E63946] rounded-full filter blur-3xl opacity-20 -translate-y-1/2 translate-x-1/2" data-id="element-153"></div>

            <h2 className="text-3xl font-bold mb-10 relative z-10" data-id="element-154">
              Lo que recibes dentro:
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 relative z-10" data-id="element-155">
              {['Cómo construir una oferta en una frase (sin marca, sin logo)', '“Kit de venta” en 45 minutos (para empezar hoy)', 'Plan de 7 días paso a paso (30–60 min/día)', 'Scripts de mensajes para ofrecer sin pena + seguimiento', 'Checklist de publicación (si eliges reventa)', 'Plantilla de medición diaria para que veas progreso real', 'Ruta de números para llegar a $100/semana (sin fantasías)'].map((item, i) => <motion.div key={i} initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.05
            }} className="flex items-start gap-3" data-id="element-156">
                  <div className="bg-[#E63946] rounded-full p-1 mt-1 flex-shrink-0" data-id="element-157">
                    <Check className="w-3 h-3 text-white" data-id="element-158" />
                  </div>
                  <span className="text-white/90 text-lg" data-id="element-159">{item}</span>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>

      {/* AUDIENCE SECTION */}
      <section className="py-20 px-4 sm:px-6 bg-[#FDF8F5]" data-id="element-160">
        <div className="max-w-5xl mx-auto" data-id="element-161">
          <h2 className="text-3xl font-bold text-[#1D3557] text-center mb-12" data-id="element-162">
            Para quién es (y para quién no)
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-163">
            {/* IS FOR YOU */}
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="bg-white p-8 rounded-2xl shadow-sm border-t-4 border-[#10B981]" data-id="element-164">
              <h3 className="text-xl font-bold text-[#1D3557] mb-6 flex items-center gap-2" data-id="element-165">
                <Check className="w-6 h-6 text-[#10B981]" data-id="element-166" />
                Es para ti si:
              </h3>
              <ul className="space-y-4" data-id="element-167">
                <li className="flex items-start gap-3 text-[#4A5568]" data-id="element-168">
                  <Check className="w-5 h-5 text-[#10B981] mt-0.5 flex-shrink-0" data-id="element-169" />
                  <span data-id="element-170">Eres mujer 35+ (USA o LATAM), hablas español.</span>
                </li>
                <li className="flex items-start gap-3 text-[#4A5568]" data-id="element-171">
                  <Check className="w-5 h-5 text-[#10B981] mt-0.5 flex-shrink-0" data-id="element-172" />
                  <span data-id="element-173">
                    Quieres ingresos extra reales sin empezar "un negocio
                    enorme".
                  </span>
                </li>
                <li className="flex items-start gap-3 text-[#4A5568]" data-id="element-174">
                  <Check className="w-5 h-5 text-[#10B981] mt-0.5 flex-shrink-0" data-id="element-175" />
                  <span data-id="element-176">
                    Odias el humo y sí haces tareas pequeñas si están claras.
                  </span>
                </li>
              </ul>
            </motion.div>

            {/* IS NOT FOR YOU */}
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="bg-white p-8 rounded-2xl shadow-sm border-t-4 border-[#E63946]" data-id="element-177">
              <h3 className="text-xl font-bold text-[#1D3557] mb-6 flex items-center gap-2" data-id="element-178">
                <X className="w-6 h-6 text-[#E63946]" data-id="element-179" />
                No es para ti si:
              </h3>
              <ul className="space-y-4" data-id="element-180">
                <li className="flex items-start gap-3 text-[#4A5568]" data-id="element-181">
                  <X className="w-5 h-5 text-[#E63946] mt-0.5 flex-shrink-0" data-id="element-182" />
                  <span data-id="element-183">Buscas hacerte rica rápido.</span>
                </li>
                <li className="flex items-start gap-3 text-[#4A5568]" data-id="element-184">
                  <X className="w-5 h-5 text-[#E63946] mt-0.5 flex-shrink-0" data-id="element-185" />
                  <span data-id="element-186">
                    No quieres vender nada (ni servicio, ni producto, ni
                    reventa).
                  </span>
                </li>
                <li className="flex items-start gap-3 text-[#4A5568]" data-id="element-187">
                  <X className="w-5 h-5 text-[#E63946] mt-0.5 flex-shrink-0" data-id="element-188" />
                  <span data-id="element-189">Quieres que alguien lo haga por ti.</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* WHY IT WORKS */}
      <section className="py-20 px-4 sm:px-6 bg-white" data-id="element-190">
        <div className="max-w-4xl mx-auto text-center" data-id="element-191">
          <h2 className="text-3xl font-bold text-[#1D3557] mb-12" data-id="element-192">
            ¿Por qué funciona cuando lo demás no?
          </h2>
          <p className="text-xl text-[#4A5568] mb-12" data-id="element-193">
            Porque esto no se basa en "ganas". Se basa en:
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6" data-id="element-194">
            {[{
            icon: <Clock className="w-8 h-8" data-id="element-195" />,
            label: 'Ejecución diaria mínima'
          }, {
            icon: <BarChart3 className="w-8 h-8" data-id="element-196" />,
            label: 'Medición'
          }, {
            icon: <Target className="w-8 h-8" data-id="element-197" />,
            label: 'Ajuste'
          }, {
            icon: <RefreshCw className="w-8 h-8" data-id="element-198" />,
            label: 'Repetición'
          }].map((item, i) => <motion.div key={i} whileHover={{
            y: -5
          }} className="flex flex-col items-center p-6 bg-[#FDF8F5] rounded-xl" data-id="element-199">
                <div className="text-[#E63946] mb-4" data-id="element-200">{item.icon}</div>
                <span className="font-bold text-[#1D3557]" data-id="element-201">{item.label}</span>
              </motion.div>)}
          </div>

          <p className="mt-12 text-lg text-[#4A5568] italic" data-id="element-202">
            "La mayoría abandona porque nunca supo qué variable cambiar. Aquí lo
            ves en papel."
          </p>
        </div>
      </section>

      {/* PRICING / CTA */}
      <section id="pricing" className="py-24 px-4 sm:px-6 bg-[#1D3557] relative overflow-hidden" data-id="element-203">
        {/* Background decorative elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0" data-id="element-204">
          <div className="absolute top-10 left-10 w-32 h-32 bg-[#E63946] rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob" data-id="element-205"></div>
          <div className="absolute bottom-10 right-10 w-32 h-32 bg-[#FAD4D8] rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000" data-id="element-206"></div>
        </div>

        <div className="max-w-3xl mx-auto text-center relative z-10" data-id="element-207">
          <motion.div initial={{
          scale: 0.9,
          opacity: 0
        }} whileInView={{
          scale: 1,
          opacity: 1
        }} viewport={{
          once: true
        }} className="bg-white rounded-3xl p-8 sm:p-12 shadow-2xl" data-id="element-208">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1D3557] mb-4" data-id="element-209">
              El Mini-Lab de Ingresos Extra
            </h2>
            <p className="text-[#4A5568] text-lg mb-8" data-id="element-210">
              Acceso inmediato al PDF + Plantillas + Scripts
            </p>

            <div className="flex flex-col items-center justify-center gap-2 mb-8" data-id="element-211">
              <div className="flex items-center gap-3" data-id="element-212">
                <span className="text-3xl text-[#9CA3AF] line-through font-medium" data-id="element-213">
                  $19
                </span>
                <span className="bg-[#E63946] text-white text-sm font-bold px-3 py-1 rounded-full" data-id="element-214">
                  -66% OFF
                </span>
              </div>
              <div className="flex items-baseline gap-2" data-id="element-215">
                <span className="text-6xl font-extrabold text-[#E63946]" data-id="element-216">
                  $6.39
                </span>
                <span className="text-[#4A5568] text-sm font-medium bg-[#FDF8F5] px-2 py-1 rounded" data-id="element-217">
                  USD
                </span>
              </div>
              <p className="text-sm text-[#10B981] font-medium mt-1" data-id="element-218">
                ¡Oferta por tiempo limitado!
              </p>
            </div>

            <button onClick={() => window.location.href = 'https://circuloactivo.com/cart/50082775138537:1'} className="w-full sm:w-auto bg-[#E63946] hover:bg-[#D62839] text-white text-xl font-bold px-12 py-5 rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1 mb-6" data-id="element-219">
              Quiero el Mini-Lab de 7 días
            </button>

            <div className="space-y-4 text-left max-w-md mx-auto mt-8 pt-8 border-t border-gray-100" data-id="element-220">
              <p className="text-[#1D3557] font-medium text-center mb-4" data-id="element-221">
                No necesitas sentirte lista. Necesitas hacer una semana
                completa.
              </p>
              <p className="text-[#4A5568] text-sm text-center" data-id="element-222">
                Si hoy estás cansada de intentar cosas sueltas y sentir que el
                dinero nunca llega… este Mini-Lab te devuelve algo que vale más
                que motivación: <strong data-id="element-223">control.</strong>
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#1D3557] text-white/60 py-12 px-4 text-center border-t border-white/10" data-id="element-224">
        <p className="mb-4" data-id="element-225">
          © {new Date().getFullYear()} Mini-Lab de Ingresos Extra.
        </p>
        <div className="flex justify-center gap-6 text-sm" data-id="element-226">
          <button onClick={openTerms} className="hover:text-white transition-colors" data-id="element-227">
            Términos
          </button>
          <button onClick={openPrivacy} className="hover:text-white transition-colors" data-id="element-228">
            Privacidad
          </button>
        </div>
      </footer>
    </div>;
}